/**
 * Methods that emit a message. Falling back to `log` keeps the output visible.
 */
const OUTPUT_METHODS = [
  'debug',
  'dir',
  'error',
  'info',
  'table',
  'trace',
  'warn',
]

/**
 * Methods that only shape how following output is displayed. There is no
 * sensible substitute, so they become no-ops.
 */
const FORMATTING_METHODS = ['clear', 'group', 'groupCollapsed', 'groupEnd']

/**
 * Add the methods `target` is missing, leaving the ones it has untouched.
 */
function backfill(target: Record<string, unknown>): void {
  const noop = (): void => undefined
  const log =
    typeof target.log === 'function' ? (target.log as () => void) : noop

  OUTPUT_METHODS.forEach((method) => {
    if (typeof target[method] !== 'function') {
      target[method] = log
    }
  })

  FORMATTING_METHODS.forEach((method) => {
    if (typeof target[method] !== 'function') {
      target[method] = noop
    }
  })

  if (typeof target.log !== 'function') {
    target.log = noop
  }
}

/**
 * Host pages sometimes swap `window.console` for a partial stub, leaving some
 * methods undefined. Calling one then throws, and the throw surfaces far from
 * its cause: the IVS SDK logs through `console[level]`, so a missing `info`
 * aborts playback with an unrelated-looking `undefined is not a function`.
 *
 * The swap can happen at any time — kao-kirei.com replaces the whole console
 * object about a second after we boot — so backfilling once is not enough. We
 * also intercept assignments to `window.console` and backfill the incoming
 * object, which keeps the host page's own console while making sure no method
 * it omits can break a feature.
 */
export function ensureConsoleMethods(): void {
  backfill(console as unknown as Record<string, unknown>)

  let current = window.console

  try {
    Object.defineProperty(window, 'console', {
      configurable: true,
      enumerable: true,
      get: () => current,
      set: (next: Console) => {
        current = next

        if (next && typeof next === 'object') {
          backfill(next as unknown as Record<string, unknown>)
        }
      },
    })
  } catch {
    // `console` is locked down on this page; the initial backfill still stands.
  }
}
